import { SidebarProvider } from "@/components/ui/sidebar";
import { SourceDrawerProvider } from "@/contexts/SourceDrawerContext";
import { HeaderPinnedModules } from "@/components/layout/HeaderPinnedModules";
import { AppFooter } from "@/components/layout/AppFooter";
import { useInterfaceMode } from "@/hooks/useInterfaceMode";
import { AppSidebar } from "./AppSidebar";
import { ContextPanel, ContextPanelProvider } from "./ContextPanel";
import { ChatContainer } from "@/components/brain/ChatContainer";
import { useAuth } from "@/hooks/useAuth";
import { useGlobalStatus } from "@/hooks/useGlobalStatus";
import DashboardStatusIndicator from "@/components/dashboard/DashboardStatusIndicator";
import CentralBrainCore from "@/components/brain/CentralBrainCore";
import FloatingReportButton from "@/components/layout/FloatingReportButton";
import BrainFreeInput from "@/components/brain/BrainFreeInput";
import NotificationBell from "@/components/layout/NotificationBell";
import PointsBadge from "@/components/layout/PointsBadge";
import ActiveSessionOverlay from "@/components/sessions/ActiveSessionOverlay";
import ActiveSessionMiniBanner from "@/components/sessions/ActiveSessionMiniBanner";
import BuildBadge from "@/components/layout/BuildBadge";
import ScrollToTopButton from "@/components/layout/ScrollToTopButton";
import MobileFloatingDock from "@/components/layout/MobileFloatingDock";
import PostRefreshFadeIn from "@/components/dashboard/PostRefreshFadeIn";
import ProfileSyncIndicator from "@/components/layout/ProfileSyncIndicator";
import FloatingInfoBanner from "@/components/ui/FloatingInfoBanner";

import { useActiveSession } from "@/hooks/useActiveSession";
import { useProfileChangeInvalidation } from "@/hooks/useProfileChangeInvalidation";
import { useJourneyUnlockFeedback } from "@/hooks/useJourneyUnlockFeedback";
import { useMemo, useState, useEffect } from "react";
import { useDisplaySettings } from "@/hooks/useDisplaySettings";
import { useLocation, useNavigate } from "react-router-dom";
import { useProfile } from "@/hooks/useProfile";
import { computePrioritizedAlert } from "@/lib/alertPriority";
import { ChevronUp, Dumbbell, Rocket, Wind, Flame, Menu, CalendarDays, Home, Users } from "lucide-react";
import PlanningDrawer from "@/components/planning/PlanningDrawer";
import { ProgramsDrawer } from "@/components/programs/ProgramsDrawer";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/components/ui/sidebar";
import { ScrollContainerProvider, useScrollContainer } from "@/hooks/useScrollContainer";

import Noise from "@/components/ui/Noise";
import Aurora from "@/components/ui/Aurora";

interface AppLayoutProps {
  children: React.ReactNode;
}

// Global toggle for ProgramsDrawer — callable from CentralBrainCore without prop drilling
let globalToggleProgramsDrawer: (() => void) | null = null;
export function toggleProgramsDrawer() {
  globalToggleProgramsDrawer?.();
}

function GlobalStatusBar() {
  const { props, loading } = useGlobalStatus();
  const location = useLocation();

  // Only show on planning-related pages (hidden on /dashboard — merged into HUD Cockpit)
  const p = location.pathname;
  const isRelevantPage = p.startsWith("/planning") || p.startsWith("/programs");
  if (loading || !isRelevantPage) return null;
  return (
    <div className="px-4 pt-2 pb-1 md:px-6">
      <div className="mx-auto max-w-lg">
        <DashboardStatusIndicator {...props} />
      </div>
    </div>
  );
}

function detectSessionType(title: string) {
  const t = title.toLowerCase();
  if (t.includes("explosiv") || t.includes("plio") || t.includes("puissance") || t.includes("vitesse")) return "explosivite";
  if (t.includes("force") || t.includes("hypertrophie") || t.includes("muscul")) return "force";
  if (t.includes("endurance") || t.includes("cardio") || t.includes("aérobie") || t.includes("course") || t.includes("running")) return "endurance";
  return "default";
}

const sessionIconConfig = {
  explosivite: { icon: Rocket, bg: "bg-amber-500/15", text: "text-amber-400", pulse: "bg-amber-500" },
  force: { icon: Dumbbell, bg: "bg-red-500/15", text: "text-red-400", pulse: "bg-red-500" },
  endurance: { icon: Wind, bg: "bg-sky-500/15", text: "text-sky-400", pulse: "bg-sky-500" },
  default: { icon: Flame, bg: "bg-primary/15", text: "text-primary", pulse: "bg-primary" },
} as const;

function CentralBrainWithAlert() {
  const { props } = useGlobalStatus();
  const alert = useMemo(() => computePrioritizedAlert(props), [props]);
  const { session, isOpen, openOverlay } = useActiveSession();

  const sType = session ? detectSessionType(session.title) : "default";
  const sConf = sessionIconConfig[sType];
  const SessionIcon = sConf.icon;

  return (
    <div className="flex items-center gap-2">
      <CentralBrainCore alertPriority={alert.priority} />
      {session && !isOpen && (
        <button
          onClick={openOverlay}
          className={cn(
            "relative flex h-9 w-9 items-center justify-center rounded-full transition-colors",
            sConf.bg, sConf.text, `hover:${sConf.bg}`
          )}
          title="Reprendre la séance"
          aria-label="Reprendre la séance en cours"
        >
          <SessionIcon className="h-4 w-4" />
          <span className={cn("absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full animate-pulse", sConf.pulse)} />
        </button>
      )}
    </div>
  );
}

function MobileMenuButton() {
  const { toggleSidebar } = useSidebar();
  return (
    <button
      onClick={toggleSidebar}
      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors md:hidden"
      aria-label="Ouvrir le menu"
    >
      <Menu className="h-5 w-5" />
    </button>
  );
}

function HeaderProfileButton() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const isMobile = useIsMobile();
  const displayName = profile?.display_name || "";
  const truncated = displayName.length > 12 ? displayName.slice(0, 12) + "…" : displayName;
  const initial = displayName ? displayName.charAt(0).toUpperCase() : "P";

  if (isMobile) {
    return (
      <button
        onClick={() => navigate("/athlete-profile")}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary hover:bg-primary/20 transition-colors min-h-[48px] min-w-[48px]"
        aria-label="Mon profil"
        title={displayName || "Mon profil"}
      >
        {initial}
      </button>
    );
  }

  return (
    <button
      onClick={() => navigate("/athlete-profile")}
      className="text-sm font-medium text-foreground hover:text-primary transition-colors cursor-pointer min-h-[48px] px-2 flex items-center"
      title={displayName || "Mon profil"}
    >
      {truncated || "Mon profil"}
    </button>
  );
}

function AppLayoutInner({ children }: AppLayoutProps) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.startsWith("/admin") || location.pathname.startsWith("/super-admin");
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [planningOpen, setPlanningOpen] = useState(false);
  const [programsDrawerOpen, setProgramsDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const { mode } = useInterfaceMode();
  const scrollRef = useScrollContainer();

  // Register global ProgramsDrawer toggle (used by CentralBrainCore)
  useEffect(() => {
    globalToggleProgramsDrawer = () => setProgramsDrawerOpen((v) => !v);
    return () => { globalToggleProgramsDrawer = null; };
  }, []);

  // Global listener: invalidate programs/planning when profile changes
  useProfileChangeInvalidation();
  useJourneyUnlockFeedback();

  const { compactMode, reducedMotion } = useDisplaySettings();

  // Apply global CSS classes to document root
  useEffect(() => {
    const html = document.documentElement;
    html.classList.toggle("perform-compact", compactMode);
    html.classList.toggle("perform-reduced-motion", reducedMotion);
    return () => {
      html.classList.remove("perform-compact", "perform-reduced-motion");
    };
  }, [compactMode, reducedMotion]);

  // Module-color contextual accent
  const MODULE_COLORS: Record<string, string> = {
    '/dashboard': '7 80% 48%',
    '/programs': '7 80% 48%',
    '/planning': '36 80% 52%',
    '/community': '217 91% 60%',
    '/library': '263 70% 58%',
    '/wellness': '5 72% 66%',
    '/athlete-profile': '5 72% 66%',
    '/achievements': '38 92% 50%',
    '/billing': '220 9% 46%',
  };

  // Aurora palette per module — 3 vivid hex stops adapted to module theme
  const AURORA_PALETTES: Record<string, [string, string, string]> = {
    '/dashboard':       ['#D9291C', '#FF6A3D', '#7c1d1d'],
    '/programs':        ['#D9291C', '#FF8A3D', '#3a0a0a'],
    '/planning':        ['#FFB347', '#FF6A3D', '#7c4a1d'],
    '/community':       ['#3D7BFF', '#7c5cff', '#1d2f7c'],
    '/library':         ['#9B5CFF', '#5c8cff', '#3d1d7c'],
    '/wellness':        ['#FF7A8A', '#FFB347', '#7c1d3d'],
    '/athlete-profile': ['#FF7A8A', '#FF5C8A', '#7c1d3d'],
    '/achievements':    ['#FFC83D', '#FF8A3D', '#7c5a1d'],
    '/billing':         ['#7c8a9b', '#5c6c7c', '#2c3a4c'],
    '/modules':         ['#5cffd3', '#3D7BFF', '#1d3d7c'],
  };
  const baseRoute = '/' + location.pathname.split('/')[1];
  const auroraColors = AURORA_PALETTES[baseRoute] ?? ['#D9291C', '#FF6A3D', '#0f3460'];

  useEffect(() => {
    const color = MODULE_COLORS[baseRoute] || '7 80% 48%';
    document.documentElement.style.setProperty('--module-color', color);
  }, [baseRoute]);

  return (
    <div className="relative flex h-screen w-full overflow-hidden">
      <div
        className="fixed inset-0 pointer-events-none z-0 overflow-hidden"
        aria-hidden="true"
        style={{ opacity: 0.32 }}
      >
        <Aurora
          key={baseRoute}
          colorStops={auroraColors}
          amplitude={1.6}
          blend={0.9}
          speed={0.7}
        />
      </div>
      <Noise
        patternAlpha={8}
        patternRefreshInterval={3}
        style={{ zIndex: 1, mixBlendMode: 'overlay', opacity: 0.4 }}
      />
      <AppSidebar />
      <div
        className="relative z-10 flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden"
      >
        {!headerCollapsed ? (
          <header className="sticky top-0 z-40 flex h-14 items-center border-b border-border/30 bg-background/60 backdrop-blur-md px-4 overflow-visible">
            <div className="flex items-center gap-1">
              {isMobile ? (
                <MobileMenuButton />
              ) : (
                <button
                  onClick={() => setHeaderCollapsed(true)}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  aria-label="Réduire la barre"
                  title="Réduire"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
              )}
              {user && (
                <>
                  <button
                    onClick={() => navigate("/dashboard")}
                    className={cn(
                      "p-1.5 rounded-md transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center",
                      location.pathname === "/dashboard"
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                    aria-label="Accueil"
                    title="Accueil"
                  >
                    <Home className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => navigate("/community")}
                    className={cn(
                      "p-1.5 rounded-md transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center",
                      location.pathname === "/community"
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                    aria-label="Communauté"
                    title="Communauté"
                  >
                    <Users className="h-5 w-5" />
                  </button>
                  <HeaderProfileButton />
                </>
              )}
            </div>
            {user && (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-visible">
                <CentralBrainWithAlert />
              </div>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              <PointsBadge />
              {mode === "advanced" && <HeaderPinnedModules />}
              {user && (
                <>
                  <button
                    onClick={() => setProgramsDrawerOpen(true)}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                    aria-label="Ouvrir les programmes"
                    title="Programmes"
                  >
                    <Dumbbell className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setPlanningOpen(true)}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                    aria-label="Ouvrir le planning"
                    title="Planning"
                  >
                    <CalendarDays className="h-5 w-5" />
                  </button>
                </>
              )}
              
              <NotificationBell />
            </div>
          </header>
        ) : (
          <button
            onClick={() => setHeaderCollapsed(false)}
            className="sticky top-0 z-40 flex items-center justify-center h-6 border-b border-border/30 bg-background/60 backdrop-blur-md text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
            aria-label="Afficher la barre"
            title="Afficher la barre"
          >
            <ChevronUp className="h-3.5 w-3.5 rotate-180" />
          </button>
        )}
        {user && <BrainFreeInput />}
        {user && !isAdmin && <GlobalStatusBar />}
        <main
          ref={scrollRef}
          className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto pb-[calc(var(--mobile-dock-h,0px)+env(safe-area-inset-bottom))] md:pb-0"
          data-scroll-container
        >
          {children}
          <AppFooter />
        </main>
      </div>
      <ContextPanel />
      <PlanningDrawer open={planningOpen} onOpenChange={setPlanningOpen} />
      <ProgramsDrawer open={programsDrawerOpen} onClose={() => setProgramsDrawerOpen(false)} />
    </div>
  );
}

export default function AppLayout({ children }: AppLayoutProps) {
  return (
    <SidebarProvider>
      <ScrollContainerProvider>
        <SourceDrawerProvider>
          <ContextPanelProvider>
            <AppLayoutInner>{children}</AppLayoutInner>
            <FloatingReportButton />
            <ChatContainer />
            <ActiveSessionOverlay />
            <ActiveSessionMiniBanner />
            <BuildBadge />
            <ScrollToTopButton />
            <ProfileSyncIndicator />
            <MobileFloatingDock />
            <PostRefreshFadeIn />
            <FloatingInfoBanner />
          </ContextPanelProvider>
        </SourceDrawerProvider>
      </ScrollContainerProvider>
    </SidebarProvider>
  );
}
